import React from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';

import { DefaultRepImageURL } from '../../containers/DefaultStyles/Shared';
import { FindRepLink, NotYourRepInfo } from '../../containers/DefaultStyles/Details';
import { OnSubmitValidator } from '../../containers/DefaultStyles/FormDetails';

import Button from '../../components/Button';
import DropDownSelect from '../DropDownSelect';
import InputGroup from '../InputGroup';
import InputGroupCustom from '../InputGroupCustom';
import TextareaGroup from '../TextareaGroup';
import TwoGroup from '../../components/TwoGroup';

import {
    relevantRepresentatives,
    joinEnglish
} from '../../utils/localRepresentative/parser';

import { triggerFindYourRep, loadRequiredFormFields } from '../../containers/LocalRep/actions';
import { cacheFormData } from '../../utils/formCaching/actions';
import { CardTypes } from '../../utils/enums';
import { findCachedValueByActionIdentifier } from '../../utils/formCaching/helpers';
import { isPortrait } from '../../utils/size';
import submissionErrorHandler from '../../utils/submissionErrorHandler';

import { requiredFormElements, formElementsLoaded } from './representatives';
import { validateStep1 } from './validations';

import { copyFor, capitalization } from '../../copy/copy';
import { Placeholders } from '../../utils/fillDynamicText';

const namePlaceholder = () => copyFor('placeholders', 'yourName', 'email');

const DetailsFormContainer = styled.form`
    ${props =>
        props.scrollableDesign
            ? ` height: 100%;`
            : ` flex: 1;
                padding: 0;
                display: flex;
                flex-direction: column;
            `};
`;

const ButtonGroup = styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    position: absolute;
    right: 0;
    bottom: 10px;
    width: 70%;

    @media (max-width: 30em) {
        width: 100%;
    }

    > div {
        position: relative;
    }

    button {
        &.submitButton {
            ${props => (props.isSubmitting ? `text-indent: -99999px;` : '')};
            margin-left: 0.5em;

            @media (max-width: 30em) {
                width: calc(50% - 0.5em);
            }
        }

        &.middleButton {
            @media (max-width: 30em) {
                width: calc(50% - 0.5em);
                margin-right: 0.5em;
            }
        }

        &:focus {
            outline: none;
        }

        + div {
            position: absolute;
            display: none;
            top: 7px;
            right: 24%;
            margin-right: -16px;
            width: 32px !important;
            height: 32px !important;
            ${props => (props.isSubmitting ? `display: block` : '')};
        }
    }
`;

const Row = styled.div`
    position: relative;
    transition: opacity 0.6s;
    ${props =>
        !isPortrait() &&
        props.senatorsNotAvailable &&
        !props.scrollableDesign &&
        `margin-top: -12px`};

    textarea {
        ${props =>
            props.senatorsNotAvailable
                ? isPortrait()
                    ? `height: 300px !important;`
                    : `height: 130px !important;`
                : null};
    }

    ${props => props.textAreaFocused && `opacity: 0`};
`;

export const getActiveTopic = (options, issue, subIssue) => {
    const issueTopics = issue && issue.senatorTopics ? issue.senatorTopics : [];
    const subIssueTopics =
        subIssue && subIssue.senatorTopics ? subIssue.senatorTopics : [];
    const matchedSubIssueTopics = subIssueTopics.filter(si => options.indexOf(si) > -1);
    if (matchedSubIssueTopics.length > 0) return matchedSubIssueTopics[0];
    const matchedIssueTopics = issueTopics.filter(si => options.indexOf(si) > -1);
    if (matchedIssueTopics.length > 0) return matchedIssueTopics[0];
    return options.find(o => o === 'Other') || null;
};

class DetailsFormMultiRepStep1 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            scrolled: false,
            textAreaFocused: false,
            valid: false,
            errors: submissionErrorHandler(props.currentFormValues, validateStep1),
            messageTextAreaFocused: false
        };
        this.clearSubmitErrors = this.clearSubmitErrors.bind(this);
        if (!props.elementsLoaded) {
            props.loadRequiredFormFields(props.repData);
        }
    }

    static propTypes = {
        actionIdentifier: PropTypes.string.isRequired,
        cacheFormData: PropTypes.func.isRequired,
        currentFormValues: PropTypes.object,
        district: PropTypes.string,
        elementsLoaded: PropTypes.bool.isRequired,
        formattedReps: PropTypes.string,
        handleSubmit: PropTypes.func.isRequired,
        hiddenRepresentatives: PropTypes.array.isRequired,
        includesTopic: PropTypes.bool.isRequired,
        includesUSSenators: PropTypes.func.isRequired,
        initialValues: PropTypes.object.isRequired,
        invalid: PropTypes.bool.isRequired,
        isActionChoiceCard: PropTypes.bool.isRequired,
        isLocalRepLoading: PropTypes.bool.isRequired,
        isSubmitting: PropTypes.bool.isRequired,
        loadRequiredFormFields: PropTypes.func.isRequired,
        onAddClick: PropTypes.func.isRequired,
        onBackFromRepAction: PropTypes.func,
        onDeleteClick: PropTypes.func.isRequired,
        onEmailMessageChange: PropTypes.func.isRequired,
        triggerFindYourRep: PropTypes.func.isRequired,
        onShowRepresentatives: PropTypes.func.isRequired,
        repData: PropTypes.object,
        representatives: PropTypes.array.isRequired,
        representativesVisibility: PropTypes.bool,
        requiredFormElements: PropTypes.array.isRequired,
        scrollableDesign: PropTypes.bool,
        senatorsNotAvailable: PropTypes.bool,
        state: PropTypes.string,
        subject: PropTypes.string,
        useLocalRepresentative: PropTypes.bool.isRequired
    };

    toggleTextAreaFocused = () => {
        if (!isPortrait() && !this.props.scrollableDesign) {
            setTimeout(
                () => {
                    this.setState({ textAreaFocused: !this.state.textAreaFocused });
                },
                !this.state.textAreaFocused ? 0 : 100
            );
        }
    };

    componentDidMount() {
        this.setState({ errors: [] });
    }

    setSubmitErrors(errors) {
        this.setState({ errors });
    }

    checkForBlurError(name) {
        const errors = submissionErrorHandler(this.props.currentFormValues, validateStep1);
        if (errors.includes(name) && !this.state.errors.includes(name))
            this.setState({ errors: [...this.state.errors, name] });
    }

    clearSubmitErrors(fieldName) {
        if (fieldName && this.state.errors.includes(fieldName)) {
            this.setState({ errors: this.state.errors.filter(e => e !== fieldName) });
        }
    }

    buttonSet = () => (
        <TwoGroup noBack={!this.props.isActionChoiceCard}>
            {this.props.isActionChoiceCard && (
                <Button
                    style={{
                        visibility: this.state.textAreaFocused ? 'hidden' : 'visible'
                    }}
                    label={copyFor('buttons', 'back', 'email', capitalization.eachWord)}
                    onClick={this.props.onBackFromRepAction}
                    className="middleButton"
                />
            )}
            <OnSubmitValidator
                onClick={e => {
                    e.preventDefault();
                    const errors = submissionErrorHandler(
                        this.props.currentFormValues,
                        validateStep1
                    );
                    if (errors.length === 0 && !this.state.textAreaFocused && !this.props.invalid) {
                        this.props.handleSubmit();
                    } else {
                        this.setSubmitErrors(errors);
                    }
                }}
            >
                <Button
                    primary={true}
                    label={
                        this.state.textAreaFocused
                            ? copyFor('buttons', 'done', 'email', capitalization.eachWord)
                            : copyFor(
                                  'buttons',
                                  'continue',
                                  'email',
                                  capitalization.eachWord
                              )
                    }
                    onClick={e => {
                        e.preventDefault();
                        if (!this.state.textAreaFocused) {
                            this.props.handleSubmit();
                        }
                    }}
                    type="submit"
                    className="submitButton"
                    disabled={this.props.invalid && !this.state.textAreaFocused}
                    isSubmitting={this.props.isSubmitting}
                />
            </OnSubmitValidator>
        </TwoGroup>
    );

    render() {
        return (
            <DetailsFormContainer
                autocomplete="on"
                scrollableDesign={this.props.scrollableDesign}
            >
                {this.props.scrollableDesign && this.props.useLocalRepresentative && (
                    <NotYourRepInfo
                        scrollableDesign={this.props.scrollableDesign}
                        isLocalRepLoading={this.props.isLocalRepLoading}
                        id="notYourRepInfo"
                    >
                        <FindRepLink onClick={this.props.triggerFindYourRep}>
                            {copyFor('findReps', 'notYour', 'email')}
                        </FindRepLink>
                    </NotYourRepInfo>
                )}
                {this.props.useLocalRepresentative &&
                    this.props.representatives.length >= 1 &&
                    this.props.includesUSSenators && (
                        <Row textAreaFocused={this.state.textAreaFocused}>
                            <Field
                                onBlur={() => this.checkForBlurError('recipient')}
                                submissionError={this.state.errors.includes('recipient')}
                                clearSubmitErrors={this.clearSubmitErrors}
                                name="recipient"
                                component={InputGroupCustom}
                                onDeleteClick={this.props.onDeleteClick}
                                onAddClick={this.props.onAddClick}
                                onShowRepresentatives={this.props.onShowRepresentatives}
                                type="text"
                                label="TO"
                                placeholder="Add Receipients"
                                representatives={this.props.representatives}
                                hiddenRepresentatives={this.props.hiddenRepresentatives}
                                representativesVisibility={
                                    this.props.representativesVisibility
                                }
                            />
                        </Row>
                    )}
                <Row textAreaFocused={this.state.textAreaFocused}>
                    {this.props.requiredFormElements
                        .filter(function(a) {
                            return a.name === 'Topic';
                        })
                        .map(a => (
                            <Field
                                key={a.index}
                                name="topic"
                                component={
                                    a.options.length > 0 ? DropDownSelect : InputGroup
                                }
                                options={a.options}
                                defaultOption={
                                    this.props.initialValues.topic || a.defaultOption
                                }
                                label="Subject"
                                type="text"
                                onChange={e => {
                                    this.props.cacheFormData(
                                        { topic: e.target.value },
                                        this.props.actionIdentifier
                                    );
                                }}
                            />
                        ))}
                </Row>
                {this.props.subject && !this.props.includesTopic && (
                    <Row>
                        <Field
                            name="subject"
                            component={InputGroup}
                            onBlur={() => this.checkForBlurError('subject')}
                            submissionError={this.state.errors.includes('subject')}
                            label="Subject"
                            type="text"
                            value={this.props.subject}
                        />
                    </Row>
                )}
                <Row
                    scrollableDesign={this.props.scrollableDesign}
                    senatorsNotAvailable={this.props.senatorsNotAvailable}
                >
                    <Field
                        title="message"
                        name="message"
                        includesTopic={this.props.includesTopic}
                        onBlur={() =>{
                            this.setState({messageTextAreaFocused: false});
                            this.checkForBlurError('message');
                        }}
                        onFocus={() => this.setState({messageTextAreaFocused: true})}
                        active={this.state.messageTextAreaFocused}
                        submissionError={this.state.errors.includes('message')}
                        clearSubmitErrors={this.clearSubmitErrors}
                        component={TextareaGroup}
                        scrollableDesign={this.props.scrollableDesign}
                        label={copyFor(
                            'formFields',
                            'messageText',
                            'general',
                            capitalization.eachWord
                        )}
                        textAreaFocused={this.state.textAreaFocused}
                        toggleTextAreaFocused={this.toggleTextAreaFocused}
                        isExpandable={!this.props.scrollableDesign}
                        senatorsNotAvailable={this.props.senatorsNotAvailable}
                        placeholder=""
                        onChange={e => {
                            const templatedVersion = e.target.value
                                .replace(
                                    `${this.props.formattedReps}`,
                                    Placeholders.LocalRepresentative
                                )
                                .replace(
                                    `${this.props.state}`,
                                    Placeholders.StatePlaceholder
                                )
                                .replace(
                                    `${this.props.district}`,
                                    Placeholders.DistrictPlaceholder
                                );

                            this.props.cacheFormData(
                                { message: templatedVersion },
                                this.props.actionIdentifier
                            );
                            this.props.onEmailMessageChange();
                        }}
                    />
                </Row>
                {this.props.scrollableDesign ? (
                    <div style={{ marginBottom: `1em` }}>{this.buttonSet()}</div>
                ) : (
                    <ButtonGroup>{this.buttonSet()}</ButtonGroup>
                )}
            </DetailsFormContainer>
        );
    }
}

export default compose(
    connect(
        state => {
            const currentCard = state.widget.cards.find(
                c => c.id === state.widget.activeAction.widgetCardId
            );
            const formElements = requiredFormElements(state);
            const includesTopic =
                formElements.filter(el => el.name === 'Topic').length > 0;
            const reps = state.localRep.localRepData
                ? relevantRepresentatives(
                      state.widget.activeAction.representativeSearch,
                      state.localRep.localRepData.representative
                  )
                : [];
            const country = ((state.localRep.localRepData || {}).address || {}).country;
            const elementsLoaded = formElementsLoaded(state, reps);
            const formattedReps = joinEnglish(
                reps
                    .filter(
                        rep =>
                            rep.canBeEmailed &&
                            state.localRep.hiddenRepresentatives.indexOf(rep.fullName) === -1
                    )
                    .map(rep => rep.fullName || rep.twitterMention)
            );
            const actionIdentifier = state.widget.activeAction.identifier;

            const cachedMessage = findCachedValueByActionIdentifier(
                state.cachedFormData.message,
                actionIdentifier
            );

            const cachedTopic = findCachedValueByActionIdentifier(
                state.cachedFormData.topic,
                actionIdentifier
            );

            const message =
                state.cachedFormData.message.length > 0 && cachedMessage !== undefined
                    ? cachedMessage
                          .replace(/@localRepresentative/g, formattedReps)
                          .replace(/@userFullName/g, namePlaceholder())
                    : state.widget.activeAction.textRaw
                          .replace(/@localRepresentative/g, formattedReps)
                          .replace(/@userFullName/g, namePlaceholder());

            const topic =
                state.cachedFormData.topic.length > 0 && !!cachedTopic
                    ? cachedTopic
                    : formElements
                          .filter(a => a.name === 'Topic')
                          .map(a =>
                              getActiveTopic(
                                  a.options,
                                  state.widget.activeAction.issue,
                                  state.widget.activeAction.subIssue
                              )
                          )[0];

            const subject = state.widget.activeAction.subject || '';

            const initialValues = {
                getUpdates: !(state.widget.endUserLocation || {}).isEU,
                message
            };

            const repData = state.localRep.localRepData || {};

            if (!!topic) {
                initialValues.topic = topic;
            } else if (formElements.find(a => a.name === 'Topic')) {
                initialValues.topic = ''; // placeholder so validation knows this is required
            }
            if (!topic && !!subject) {
                initialValues.subject = subject;
            }

            return {
                actionIdentifier,
                elementsLoaded,
                formattedReps,
                includesTopic,
                subject,
                initialValues,
                repData,
                district: repData.district || 'your district',
                state: repData.stateName || 'your state',
                isActionChoiceCard: currentCard.type === CardTypes.actionChoice,
                isLocalRepLoading: state.localRep.isLocalRepLoading,
                requiredFormElements: formElements,

                representatives: reps
                    .filter(
                        rep =>
                            rep.canBeEmailed &&
                            state.localRep.hiddenRepresentatives.indexOf(rep.fullName) ===
                                -1
                    )
                    .map((a, index) => ({
                        displayName: a.displayName,
                        fullName: a.fullName,
                        photoUrl: a.photoUrl || DefaultRepImageURL(country),
                        index: index
                    })),
                hiddenRepresentatives: reps
                    .filter(
                        rep =>
                            rep.canBeEmailed &&
                            state.localRep.hiddenRepresentatives.indexOf(rep.fullName) !==
                                -1
                    )
                    .map((a, index) => ({
                        fullName: a.fullName,
                        photoUrl: a.photoUrl || DefaultRepImageURL(country),
                        index: index
                    })),
                representativesVisibility: state.localRep.representativesVisibility,
                senatorsNotAvailable:
                    state.localRep.localRepData.representative !== undefined
                        ? state.localRep.localRepData.representative.length < 2
                        : false,
                currentFormValues: (state.form['emailDetailsFormMultiRepStep1'] || {})
                    .values
            };
        },
        {
            cacheFormData,
            loadRequiredFormFields,
            triggerFindYourRep
        }
    ),
    reduxForm({
        destroyOnUnmount: true,
        enableReinitialize: true,
        form: 'emailDetailsFormMultiRepStep1',
        validate: validateStep1
    })
)(DetailsFormMultiRepStep1);
